/** Local terrain structure in metres, shared by meshing, drainage and collision.
 * Continuous planet-space fields have no region/tile ownership or LOD input. */
import { SimplexNoise3D } from './noise.js';
import { hashSeed, rngFromString } from './rng.js';
import type { PlanetDef, PlanetArchetype } from './universe.js';

type Profile = { span:number; rise:number; shelf:number; angular:number };
const PROFILES:Record<PlanetArchetype,Profile> = {
  terran: {span:240,rise:38,shelf:10,angular:.42},
  ocean: {span:260,rise:24,shelf:6,angular:.28},
  desert: {span:330,rise:36,shelf:12,angular:.56},
  ice: {span:310,rise:24,shelf:4,angular:.16},
  volcanic: {span:230,rise:34,shelf:12,angular:.72},
  barren: {span:230,rise:30,shelf:10,angular:.62},
};
const smooth=(v:number):number=>{const t=Math.max(0,Math.min(1,v));return t*t*(3-2*t);};
function latticeValue(x:number,y:number,z:number,seed:number):number {
  let h=seed^Math.imul(x,374761393)^Math.imul(y,668265263)^Math.imul(z,1442695041);
  h=Math.imul(h^(h>>>13),1274126177);h^=h>>>16;
  return (h>>>0)/2147483648-1;
}

/** Continuous piecewise-planar value field. Four corners of each tetrahedron
 * give broad planes; shared vertex values make every internal boundary meet.
 * Coordinates are rotated per planet so the lattice doesn't follow cube faces. */
export function planarField(x:number,y:number,z:number,seed:number):number {
  const ix=Math.floor(x),iy=Math.floor(y),iz=Math.floor(z),a=x-ix,b=y-iy,c=z-iz;
  const v0=latticeValue(ix,iy,iz,seed),v3=latticeValue(ix+1,iy+1,iz+1,seed);
  if(a>=b) {
    if(b>=c)return v0*(1-a)+latticeValue(ix+1,iy,iz,seed)*(a-b)+latticeValue(ix+1,iy+1,iz,seed)*(b-c)+v3*c;
    if(a>=c)return v0*(1-a)+latticeValue(ix+1,iy,iz,seed)*(a-c)+latticeValue(ix+1,iy,iz+1,seed)*(c-b)+v3*b;
    return v0*(1-c)+latticeValue(ix,iy,iz+1,seed)*(c-a)+latticeValue(ix+1,iy,iz+1,seed)*(a-b)+v3*b;
  }
  if(a>=c)return v0*(1-b)+latticeValue(ix,iy+1,iz,seed)*(b-a)+latticeValue(ix+1,iy+1,iz,seed)*(a-c)+v3*c;
  if(b>=c)return v0*(1-b)+latticeValue(ix,iy+1,iz,seed)*(b-c)+latticeValue(ix,iy+1,iz+1,seed)*(c-a)+v3*a;
  return v0*(1-c)+latticeValue(ix,iy,iz+1,seed)*(c-b)+latticeValue(ix,iy+1,iz+1,seed)*(b-a)+v3*a;
}

export class LocalLandforms {
  private readonly broad:SimplexNoise3D;
  private readonly regional:SimplexNoise3D;
  private readonly seed:number;
  private readonly rotation:number[];
  private readonly profile:Profile;
  readonly enabled:boolean;
  constructor(private readonly def:PlanetDef) {
    this.seed=hashSeed(`${def.seed}:local-landforms:v1`);
    this.broad=new SimplexNoise3D(this.seed);
    this.regional=new SimplexNoise3D(this.seed^0x53484c46);
    this.profile=PROFILES[def.archetype];
    // Authored flat home/fixture terrain explicitly opts out through its tiny
    // terrain amplitude. Ordinary planets and moons all use human-scale forms.
    this.enabled=def.terrain.amplitudeRel>=.001;
    const rng=rngFromString(`${def.seed}:landform-frame`);
    let ax=rng()-.5,ay=rng()-.5,az=rng()-.5;
    const length=Math.sqrt(ax*ax+ay*ay+az*az)||1;ax/=length;ay/=length;az/=length;
    let bx=Math.abs(ay)<.9?-az:0,by=Math.abs(ay)<.9?0:az,bz=Math.abs(ay)<.9?ax:-ay;
    const bl=Math.sqrt(bx*bx+by*by+bz*bz)||1;bx/=bl;by/=bl;bz/=bl;
    this.rotation=[ax,ay,az,bx,by,bz,ay*bz-az*by,az*bx-ax*bz,ax*by-ay*bx];
  }

  /** Offset from the existing macro terrain, before river/settlement carving.
   * A shore envelope preserves submerged beds and fades continuously inland. */
  offset(dx:number,dy:number,dz:number,heightM:number):number {
    if(!this.enabled)return 0;
    const above=this.def.seaRadius===null?1e6:this.def.radius+heightM-this.def.seaRadius;
    if(above<=1)return 0;
    const shore=smooth((above-1)/18),r=this.def.radius,m=this.rotation,p=this.profile;
    const x=(dx*m[0]+dy*m[1]+dz*m[2])*r,y=(dx*m[3]+dy*m[4]+dz*m[5])*r,z=(dx*m[6]+dy*m[7]+dz*m[8])*r;
    // Quiet corridors and stronger shoulders occupy several hundred metres;
    // a two-octave rolling field supplies hills instead of per-step bumps.
    const region=this.regional.noise(x/680,y/680,z/680);
    const strength=.42+.58*smooth((region+.5)/1.1);
    const roll=this.broad.fbm(x/p.span,y/p.span,z/p.span,2,2,.3);
    const plane=planarField(x/115,y/115,z/115,this.seed);
    const shape=roll*(1-p.angular)+plane*p.angular;
    // A finite ramp through a broad coherent carrier gives a short face with
    // shelves on either side. Varying the threshold avoids global height rings.
    const carrier=planarField(x/175+7.3,y/175-2.1,z/175+4.7,this.seed^0x42414e4b);
    const threshold=region*.18;
    const bank=smooth((carrier-threshold+.055)/.11)-.5;
    const shelfMask=smooth((region+.35)/.7);
    const metres=(p.rise*shape+p.shelf*bank*shelfMask)*strength;
    // Even a negative hollow cannot punch through the preserved shoreline.
    return Math.max(-above*.55,metres*shore);
  }
}
